import { setActivePinia } from 'pinia'
import { navigateTo, useNuxtApp } from 'nuxt/app'

import {
  clearAdminPreviewTabArtifacts,
  createAdminPreviewEntryNonce,
  getAdminV2PreviewAccessToken,
  hasAdminPreviewTabArtifacts,
  isAdminPreviewTabRecordActive,
  isCurrentAdminPreviewEntry,
  readAdminPreviewAgencyToken,
  readAdminPreviewTabRecord,
  readAdminV2PreviewEnvelope,
  resolveAdminPreviewExpiry,
  writeAdminPreviewAgencyToken,
  writeAdminPreviewTabRecord,
  type AdminPreviewTabRecord,
} from '@common/core-layer/utils/admin-preview-token.utils'
import {
  clearAdminPreviewMarkerCookieForLocation,
  writeAdminPreviewMarkerCookie,
} from '@common/core-layer/utils/admin-preview-cookie.utils'
import {
  readBuilderIamPreviewCredential,
  readJwtExpiryMs,
  readLegacyAdminPreviewCredential,
} from '@common/core-layer/utils/admin-preview-auth.utils'

import {
  isCoursesAdminPreviewRoute,
  isV2PreviewToken,
  mergeCoursesAdminPreviewQuery,
  parseCoursesAdminPreviewContextFromQuery,
  parseCoursesAdminPreviewFromQuery,
  stripAdminPreviewTokenFromUrl,
} from '../utils/courses-admin-preview-url.utils'
import { removeCoursesAdminPreviewQueries, resetCoursesAdminPreviewQueries } from '../utils/courses-admin-preview-query-cache.utils'
import { useCoursesAdminPreview } from '../composables/useCoursesAdminPreview'
import { useCoursesAdminPreviewStore } from '../stores/courses-admin-preview.ui-store'

import type { Pinia } from 'pinia'
import type { QueryClient } from '@tanstack/vue-query'
import type { CoursesAdminPreviewCredential, CoursesAdminPreviewRouteQuery } from '../utils/courses-admin-preview-url.utils'

type CoursesAdminPreviewStore = ReturnType<typeof useCoursesAdminPreviewStore>

/**
 * Leave the preview: clear the store, this tab's per-tab artifacts (record, `acatv2`,
 * `acatv2-agency`) and cached courses queries. The browser-wide `capm` marker is cleared only while
 * it still points at THIS tab's location, so exiting here never ends another tab's preview.
 */
function exitAdminPreview(store: CoursesAdminPreviewStore, queryClient: QueryClient | undefined): void {
  const locationId = readAdminPreviewTabRecord()?.locationId ?? store.previewLocationId
  store.clearAdminPreview()
  clearAdminPreviewTabArtifacts()
  clearAdminPreviewMarkerCookieForLocation(locationId)
  removeCoursesAdminPreviewQueries(queryClient)
}

/**
 * SSR reload of a `?is_preview&location_id=` URL: the server can't see the client-only admin
 * session, but it must render the same preview shell the client will (the Gate's `ClientOnly`
 * fallback, learner self-fetch skipped) to avoid a hydration mismatch. The client then rehydrates
 * and settles the status to `ready` / `failed`. No token is involved.
 */
function hydrateSsrPreviewContext(store: CoursesAdminPreviewStore, query: CoursesAdminPreviewRouteQuery): void {
  const context = parseCoursesAdminPreviewContextFromQuery(query)
  if (!context) return
  store.setAdminPreviewContext(context)
  store.setSessionStatus('exchanging')
}

/** Restore the credential named by this tab's record into the store. False when it is gone / expired. */
function restoreRecordCredential(store: CoursesAdminPreviewStore, record: AdminPreviewTabRecord): boolean {
  if (!isAdminPreviewTabRecordActive(record)) return false

  if (record.mode === 'builderIam') {
    const builderIam = readBuilderIamPreviewCredential(record)
    if (builderIam) store.setAdminPreviewToken(builderIam.token)
    return !!builderIam
  }

  if (record.mode === 'acatv2') {
    const envelope = readAdminV2PreviewEnvelope()
    if (!getAdminV2PreviewAccessToken(envelope)) return false
    store.setAdminSession(envelope)
    // Restore the raw agency token so the federated remote can re-run its sign-in exchange on
    // reload (it can't authenticate with the exchanged admin access token).
    const agencyToken = readAdminPreviewAgencyToken()
    if (agencyToken) store.setAdminPreviewToken(agencyToken)
    return true
  }

  return !!readLegacyAdminPreviewCredential({ locationId: record.locationId })
}

/**
 * Reload / post-token-strip: restore THIS tab's admin session from its `cp-admin-preview` record
 * without another exchange. The record's mode is the remote precedence (builder IAM → `acatv2` →
 * `acat`) resolved when the tab entered the preview. Client-only. Returns whether this tab is in a
 * preview (an expired / emptied record still is — its session is marked `failed`).
 */
function rehydrateAdminPreviewSession(store: CoursesAdminPreviewStore, query: CoursesAdminPreviewRouteQuery): boolean {
  const context = parseCoursesAdminPreviewContextFromQuery(query)
  const hydrate = (locationId: string) =>
    store.setAdminPreviewContext({
      locationId,
      builder: context?.builder ?? false,
      templateId: context?.templateId ?? null,
      companyId: context?.companyId ?? null,
    })

  const record = readAdminPreviewTabRecord()
  if (record) {
    hydrate(record.locationId)
    store.setAuthMode(record.mode)
    store.setSessionStatus(restoreRecordCredential(store, record) ? 'ready' : 'failed')
    return true
  }

  // No preview entered in this tab. A remote-minted legacy `acat` (browser-wide) may start one, but
  // only when the URL explicitly asks for it (`is_preview` + matching `location_id`), so a leftover
  // `acat` never hijacks a learner visit or another tab.
  const legacyAdmin = context ? readLegacyAdminPreviewCredential({ locationId: context.locationId }) : null
  if (!legacyAdmin) return false
  writeAdminPreviewTabRecord({
    mode: 'acat',
    locationId: legacyAdmin.locationId,
    exp: resolveAdminPreviewExpiry(legacyAdmin.expiresAt),
    nonce: createAdminPreviewEntryNonce(),
  })
  hydrate(legacyAdmin.locationId)
  store.setAuthMode('acat')
  store.setSessionStatus('ready')
  writeAdminPreviewMarkerCookie({ locationId: legacyAdmin.locationId, expiresAt: legacyAdmin.expiresAt })
  return true
}

/**
 * Run the `v2.` exchange for the entry identified by `nonce`. Not awaited by the middleware, so the
 * token is scrubbed from the URL first; a newer entry or an exit replaces the record, which makes
 * this (and the composable's writes) a no-op.
 */
async function runAdminPreviewExchange(
  store: CoursesAdminPreviewStore,
  credential: CoursesAdminPreviewCredential,
  nonce: string,
  queryClient: QueryClient | undefined
): Promise<void> {
  const { exchangeAdminPreviewSession } = useCoursesAdminPreview()
  const session = await exchangeAdminPreviewSession(credential.token, credential.locationId, credential.companyId, {
    entryNonce: nonce,
  })
  if (!isCurrentAdminPreviewEntry(nonce)) return
  store.setSessionStatus(session ? 'ready' : 'failed')
  // Queries mounted while the exchange was in flight ran fail-closed; refetch them as the admin.
  if (session) await resetCoursesAdminPreviewQueries(queryClient)
}

/**
 * Fresh entry (client): replace any previous preview in this tab with the new credential and write
 * this tab's record. Returns the pending `v2.` exchange, if any.
 */
function startAdminPreviewEntry(
  store: CoursesAdminPreviewStore,
  credential: CoursesAdminPreviewCredential,
  queryClient: QueryClient | undefined
): Promise<void> | null {
  // Capture the agency Bearer for the federated remote's own sign-in exchange (it can't authenticate
  // with the exchanged admin access token). Always overwrite: a full credential is a fresh entry,
  // possibly a same-tab switch to a DIFFERENT preview — not overwriting would leave the remote
  // authenticating as the previous preview's agency.
  store.setAdminPreviewToken(credential.token)

  // Drop the previous preview's per-tab artifacts and every cached courses query (admin or learner)
  // so neither a stale builder JWT / envelope nor stale data can outrank this entry.
  const nonce = createAdminPreviewEntryNonce()
  clearAdminPreviewTabArtifacts()
  store.setAdminSession(null)
  removeCoursesAdminPreviewQueries(queryClient)

  if (isV2PreviewToken(credential.token)) {
    // IAM v2 admin preview: exchange the `v2.` magic-link token on every fresh full credential
    // (builder re-entry / theme switch carries a new `company_id`). The pending record marks this tab
    // as previewing right away, so courses calls fail closed (never the learner token) until the
    // exchange writes `acatv2`, the agency token, the final record and the `capm` marker.
    writeAdminPreviewTabRecord({ mode: 'acatv2', locationId: credential.locationId, exp: resolveAdminPreviewExpiry(null), nonce })
    store.setAuthMode('acatv2')
    store.setSessionStatus('exchanging')
    return runAdminPreviewExchange(store, credential, nonce, queryClient)
  }

  // Builder IAM (CRM agency JWT): no exchange — membership-backend verifies the JWT and its agency →
  // location access via IAM on every request (mirrors the remote's `enableBuilderIamAuth`). The
  // record expires with the JWT (`exp` claim, else the default TTL); an expired JWT fails at once.
  const expiresAt = readJwtExpiryMs(credential.token)
  writeAdminPreviewAgencyToken(credential.token)
  writeAdminPreviewTabRecord({
    mode: 'builderIam',
    locationId: credential.locationId,
    exp: expiresAt ?? resolveAdminPreviewExpiry(null),
    nonce,
  })
  writeAdminPreviewMarkerCookie({ locationId: credential.locationId, expiresAt })
  store.setAuthMode('builderIam')
  store.setSessionStatus(expiresAt !== null && expiresAt <= Date.now() ? 'failed' : 'ready')
  return null
}

/**
 * Courses admin-preview session hydration (clientportal). Supports every admin credential the
 * federated remote supports: `v2.` magic link → `acatv2` exchange, raw agency JWT → builder IAM, and a
 * remote-minted legacy `acat`. Learner `cat` / `catv2` are never touched.
 *
 * `00.` prefix runs this before `auth.global` and `courses-access-guard.global` so they see a hydrated preview session.
 * Scope: TOP-LEVEL `/courses` only. A fresh, context-gated entry writes this tab's `cp-admin-preview`
 * record (per-tab sessionStorage) — the only thing that lets `BaseService` use an admin credential —
 * so other tabs (and learner visits) are never affected by the browser-wide `acat` / `capm`.
 *
 * Admin sessions live in `sessionStorage` (client-only), so there is no SSR admin-auth path; SSR only
 * mirrors the non-sensitive preview context from the URL so it renders the same shell as the client.
 */
export default defineNuxtRouteMiddleware(async to => {
  const nuxtApp = useNuxtApp()
  const pinia = nuxtApp.$pinia as Pinia | undefined
  if (!pinia) {
    return
  }

  setActivePinia(pinia)

  const store = useCoursesAdminPreviewStore(pinia)
  const queryClient = process.client ? (nuxtApp.$queryClient as QueryClient | undefined) : undefined

  // Leaving /courses ends this tab's preview (scope isolation); falls back to normal auth. Storage is
  // checked too: after a hard navigation / reload onto a non-courses page the store is empty while
  // this tab's sessionStorage still holds the preview artifacts.
  if (!isCoursesAdminPreviewRoute(to.path)) {
    if (store.isAdminPreview || store.adminSession || (process.client && hasAdminPreviewTabArtifacts())) {
      exitAdminPreview(store, queryClient)
    }
    return
  }

  // Fresh admin-preview entry (token + location_id + is_preview in the URL). Once the token is
  // stripped, subsequent /courses navigations carry no credential and reuse the tab's session.
  const credential = parseCoursesAdminPreviewFromQuery(to.query)
  if (!credential) {
    if (!process.client) {
      hydrateSsrPreviewContext(store, to.query)
      return
    }

    // Reload / post-token-strip: no token in the URL, but this tab's admin session may still be in
    // storage (survives reload). Re-hydrate the store without another exchange.
    if (!store.authMode) {
      if (rehydrateAdminPreviewSession(store, to.query)) {
        // SSR (learner-authenticated) data may have been dehydrated into the cache.
        removeCoursesAdminPreviewQueries(queryClient)
      } else if (store.isAdminPreview) {
        // SSR rendered the preview shell from the URL, but this tab holds no preview session.
        store.setSessionStatus('failed')
      }
    }

    // Internal remote/host navigation may construct a new query instead of carrying the current
    // preview context. Restore only the non-sensitive allowlisted keys while the per-tab admin
    // session is active so `company_id` remains available to the remote's reload exchange.
    if (store.isAdminPreview && store.previewLocationId) {
      const mergedQuery = mergeCoursesAdminPreviewQuery(to.query, {
        locationId: store.previewLocationId,
        builder: store.builderMode,
        templateId: store.templateId,
        companyId: store.companyId,
      })
      const queryChanged = [...new Set([...Object.keys(to.query), ...Object.keys(mergedQuery)])].some(
        key => JSON.stringify(mergedQuery[key]) !== JSON.stringify(to.query[key])
      )
      if (queryChanged) {
        return navigateTo({ path: to.path, query: mergedQuery, hash: to.hash }, { replace: true })
      }
    }
    return
  }

  store.setAdminPreviewContext({
    locationId: credential.locationId,
    builder: credential.builder,
    templateId: credential.templateId,
    companyId: credential.companyId,
  })

  // Everything below is client-only: the agency Bearer must never enter server-side Pinia state
  // (it would be serialized into the SSR payload) — the session lives in sessionStorage.
  if (!process.client) {
    store.setSessionStatus('exchanging')
    return
  }

  const pendingExchange = startAdminPreviewEntry(store, credential, queryClient)

  // Scrub the credential from the address bar BEFORE the exchange settles (router replace,
  // preserving is_preview/location_id/builder/template_id/company_id for reload). The replacement
  // re-enters this middleware without a credential and passes through on the hydrated store; the
  // exchange settles in the background, guarded by the entry nonce.
  const strippedUrl = stripAdminPreviewTokenFromUrl(to.fullPath)
  if (strippedUrl !== to.fullPath) {
    return navigateTo(strippedUrl, { replace: true })
  }
  await pendingExchange
})
